import Button from '@rio-cloud/rio-uikit/Button';
import classNames from 'classnames';
import { mapValues } from 'es-toolkit/object';
import { FunctionComponent, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { runInBackgroundCallback } from '../../../../../configuration/setup/backgroundActions';
import { useAppDispatch } from '../../../../../configuration/setup/hooks';
import { usePutDepartureCompletionMutation } from '../../../api/compoundManagementApi';
import { showSuccessNotification } from '../../../notifications/SuccessNotification';
import { compoundManagementSlice } from '../../../reducers/compoundManagementSlice';
import { MeansOfTransportType } from '../../../sharedComponents/common.types';
import { TransportInformation } from '../../compoundManagement.types';
import { CompletedStatusAtInput } from './CompletedStatusAtInput';
import { DetailsFormHighlight } from './DetailsFormHighlight';
import { MeansOfTransportTypeInput } from './formInputs/MeansOfTransportTypeInput';
import { isTransportInformation, TransportInformationInputs } from './formInputs/TransportInformationInputs';
import { getStatusDateValidationMessageId } from './validateStatusDate';
import {
    getMeansOfTransportIdValidationMessageId,
    getMeansOfTransportTypeValidationMessageId,
} from './validateTransportInformation';
import { handleDepartureCompletionError } from '../DepartureErrorHandling';

type DepartureCompletedFormProps = {
    timeZone: string;
    compoundVehicleId: string;
    meansOfTransportTypeInputDisabled: boolean;
    disabled: boolean;
    departureCompletedAt: Date | undefined;
    transportInformation: TransportInformation | undefined;
};

export const DepartureCompletedForm: FunctionComponent<DepartureCompletedFormProps> = ({
    timeZone,
    compoundVehicleId,
    meansOfTransportTypeInputDisabled,
    disabled,
    departureCompletedAt: previousDepartureCompletedAt,
    transportInformation: previousTransportInformation,
}) => {
    const {
        formState,
        isLoading,
        validationErrorMessageIds,
        setDepartureCompletedAt,
        mergeChangedTransportInformation,
        handleConfirmDeparture,
    } = useDepartureCompletedForm(compoundVehicleId, {
        departureCompletedAt: previousDepartureCompletedAt,
        transportInformation: previousTransportInformation ?? {},
    });

    const isUpdate = previousDepartureCompletedAt !== undefined;

    const onMeansOfTransportTypeChanged = (meansOfTransportType: MeansOfTransportType | undefined) => {
        mergeChangedTransportInformation({ meansOfTransportType });
    };

    return (
        <DetailsFormHighlight disabled={disabled}>
            <CompletedStatusAtInput
                labelId="outboundOrderBook.compoundManagement.table.heading.departureDate"
                timeZone={timeZone}
                disabled={disabled}
                statusDate={formState.departureCompletedAt}
                onChangeStatusDate={setDepartureCompletedAt}
                validationErrorId={validationErrorMessageIds.departureCompletedAt}
            />
            <MeansOfTransportTypeInput
                disabled={meansOfTransportTypeInputDisabled}
                value={formState.transportInformation.meansOfTransportType}
                onMeansOfTransportTypeChanged={onMeansOfTransportTypeChanged}
                validationErrorId={validationErrorMessageIds.meansOfTransportType}
            />
            {isTransportInformation(formState.transportInformation) && (
                <TransportInformationInputs
                    transportInformation={formState.transportInformation}
                    setTransportInformation={mergeChangedTransportInformation}
                    validationErrorMessageIds={validationErrorMessageIds}
                    disabled={disabled}
                />
            )}
            <hr className="width-100pct grid-column-span-2" />
            <div className="grid-column-span-2 display-flex gap-25 justify-content-end">
                {isUpdate ? (
                    <UpdateDepartureButton disabled={disabled} isLoading={isLoading} onClick={handleConfirmDeparture} />
                ) : (
                    <ConfirmDepartureButton
                        disabled={disabled}
                        isLoading={isLoading}
                        onClick={handleConfirmDeparture}
                    />
                )}
            </div>
        </DetailsFormHighlight>
    );
};

type DepartureCompletedFormState = {
    departureCompletedAt: Date | string | undefined;
    transportInformation: Partial<TransportInformation>;
};

const useDepartureCompletedForm = (compoundVehicleId: string, previousState: DepartureCompletedFormState) => {
    const dispatch = useAppDispatch();
    const [completeDeparture, completeDepartureRequest] = usePutDepartureCompletionMutation();

    const [submitted, setSubmitted] = useState(false);
    const [formState, setFormState] = useState<DepartureCompletedFormState>(previousState);

    const validationErrorMessageIds = {
        departureCompletedAt: getStatusDateValidationMessageId(formState.departureCompletedAt),
        meansOfTransportType: getMeansOfTransportTypeValidationMessageId(formState.transportInformation),
        meansOfTransportId: getMeansOfTransportIdValidationMessageId(formState.transportInformation),
    };

    const setDepartureCompletedAt = (departureCompletedAt: Date | string | undefined) =>
        setFormState((previous) => ({ ...previous, departureCompletedAt }));

    const mergeChangedTransportInformation = (change: Partial<TransportInformation>) =>
        setFormState((previous) => ({
            ...previous,
            transportInformation: { ...previous.transportInformation, ...change },
        }));

    const handleConfirmDeparture = runInBackgroundCallback(async () => {
        setSubmitted(true);

        const isValid = Object.values(validationErrorMessageIds).every((messageId) => messageId === undefined);
        if (isValid) {
            try {
                await completeDeparture({
                    statusAt: formState.departureCompletedAt as Date,
                    transportInformation: formState.transportInformation as TransportInformation,
                    compoundVehicleIds: [compoundVehicleId],
                }).unwrap();

                const isUpdate = previousState.departureCompletedAt !== undefined;
                showSuccessNotification(
                    isUpdate
                        ? 'outboundOrderBook.compoundManagement.notification.departedVehicle.updateDeparture.success'
                        : 'outboundOrderBook.compoundManagement.notification.outgoingVehicle.confirmDeparture.success',
                );
                dispatch(compoundManagementSlice.actions.deselectCompoundVehicle());
            } catch (error: unknown) {
                handleDepartureCompletionError(error);
            }
        }
    });

    return {
        isLoading: completeDepartureRequest.isLoading,
        formState,
        validationErrorMessageIds: mapValues(validationErrorMessageIds, (messageId) =>
            submitted ? messageId : undefined,
        ),
        setDepartureCompletedAt,
        mergeChangedTransportInformation,
        handleConfirmDeparture,
    };
};

const ConfirmDepartureButton: FunctionComponent<{ disabled: boolean; isLoading: boolean; onClick: () => void }> = ({
    disabled,
    isLoading,
    onClick,
}) => (
    <Button
        className={classNames({ 'btn-loading': isLoading })}
        disabled={disabled || isLoading}
        iconName="rioglyph-load-unload"
        bsStyle={Button.PRIMARY}
        onClick={onClick}
    >
        <FormattedMessage id="outboundPortal.compoundManagement.button.confirmDeparture" />
    </Button>
);

const UpdateDepartureButton: FunctionComponent<{ disabled: boolean; isLoading: boolean; onClick: () => void }> = ({
    disabled,
    isLoading,
    onClick,
}) => (
    <Button
        className={classNames({ 'btn-loading': isLoading })}
        disabled={disabled || isLoading}
        iconName="rioglyph-refresh"
        bsStyle={Button.DEFAULT}
        onClick={onClick}
    >
        <FormattedMessage id="outboundPortal.compoundManagement.button.updateDeparture" />
    </Button>
);
